import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core';

import { getAbsoluteAssetPath, getCSSVariable, isNumber } from '../../../services/utils';

@Component({
  tag: 'pk-avatar',
  assetsDirs: ['fallback'],
  styleUrl: './scss/pk-avatar.scss',
  shadow: true,
})
export class PkAvatar {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkAvatarElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() _fallbackImage: string = null;
  @State() _customBg: string = null;

  // Public Property API
  // ========================

  /** Set the fallback image to use */
  @Prop({ reflect: true }) fallbackImage: string = 'generic-head';

  /** Set a background color. The value should be a valid value of the palette color */
  @Prop({ reflect: true }) backgroundColor: string;

  /** Set a custom background (ex: gradient).
   * Please, notice that background color will take priority over the custom background-color. */
  @Prop({ reflect: true }) customBackground: string;

  /** Set the color of the icon element. The value should be a valid value of the palette color. */
  @Prop({ reflect: true }) iconColor: string;

  /** If true, the element will be shown with disable color style. */
  @Prop({ reflect: true }) iconDisabled: boolean = false;

  /** Name of the icon to load. */
  @Prop({ reflect: true }) iconName: string;

  /** Size of the avatar. */
  @Prop({ reflect: true, mutable: true }) size: number;

  /** Url to load the avatar image from (this can be also a base64 encoded image).  */
  @Prop({ reflect: true }) src: string;

  /** Specify the type of the avatar to render */
  @Prop({ reflect: true }) type: 'image' | 'icon' | 'text';

  // Prop lifecycle events
  // =======================

  @Watch('fallbackImage')
  handleFallbackImageProp() {
    if (this.fallbackImage === 'generic-head') {
      this._fallbackImage = getAbsoluteAssetPath(`./fallback/${this.fallbackImage}` + '.svg');
    } else {
      if (this.fallbackImage) {
        console.warn(`[Pk-Avatar]: When used, 'fallbackImage' property should be 'generic-head'.`);
      }
      this._fallbackImage = null;
    }
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Handler to be called when the Avatar is clicked */
  @Event() pkAvatarClick: EventEmitter<UIEvent>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  connectedCallback() {
    this.setInitialSize();
  }

  componentWillLoad() {
    this.handleFallbackImageProp();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleOnClick = (ev?: UIEvent) => this.pkAvatarClick.emit(ev);

  private setInitialSize = () => {
    /**
     * Setting the initial value in the prop will create an infinite loop
     * to avoid that we need to force the initial value when the compoonent gets connected
     * to the DOM, if no `size` property has been provided.
     */
    this.size = this.size && isNumber(this.size) ? this.size : null;
  };

  /**
   * Avatar text should allow custom background colors (color not being part of Panenka paletter color, e.g. gradients)
   * Since we need to provide a reliable way for users to still set the background using the background-color tokens,
   * `backgroundColor` prop will take higher priority over the `customBackground` property, in other words,
   * the custom background color will take the background color token value if present.
   */
  private get customBg() {
    return this.backgroundColor ? getCSSVariable(this.backgroundColor) : this.customBackground;
  }

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.customBg && { '--pk-avatar--custom-bg': this.customBg }),
      ...(this.size && { '--pk-avatar--size': `${this.size}px` }),
      ...(this.backgroundColor && {
        '--pk-avatar--bg': getCSSVariable(this.backgroundColor),
      }),
    };

    return (
      <Host style={{ ...style }}>
        <button
          type="button"
          class={{ 'pk-avatar': true, [`type-${this.type}`]: true }}
          onClick={this.handleOnClick}
          part="container"
        >
          {this.type === 'image' && (
            <pk-image
              part="image"
              class="h-[--avatar--size] w-[--avatar--size] overflow-hidden rounded-[50%]"
              src={this.src}
              fallbackImage={this._fallbackImage}
              width={this.size}
              height={this.size}
              alt=""
            />
          )}
          {this.type === 'icon' && (
            <pk-icon
              class="absolute right-1/2 top-1/2 inline-flex -translate-y-[50%] translate-x-[50%]"
              color={this.iconColor}
              name={this.iconName}
              disabled={this.iconDisabled}
              size={Math.round(Number(this.size) / 2)}
              part="icon"
            />
          )}
          {this.type === 'text' && (
            <div
              class="pk-avatar--text h-[--avatar--size] w-[--avatar--size] overflow-hidden rounded-[50%]"
              part="text"
            >
              <slot name="text" />
            </div>
          )}
          <div
            class="pk-avatar--badge absolute left-full top-full flex -translate-x-full -translate-y-full"
            part="badge"
          >
            <slot name="badge" />
          </div>
        </button>
      </Host>
    );
  }
}
